/**
 * @file schemas/client-lookups.ts
 * @description 客戶字典 Zod 校驗 schema（驗證類型 / 驗證資格 / 驗證類別 / 驗證方案 / 合約來源）。
 *
 * 各字典共用同一套基礎欄位（description / sortOrder / isActive），
 * 由 lookupBaseShape 衍生出各組 create/update schema，差異僅在 name 長度與額外欄位：
 *  - verificationTypes.name max = 50（與 clients.verificationType[] 對齊）
 *  - verificationQualifications.name max = 50（與 clients.verificationQualification 對齊）
 *  - verificationCategories.name max = 30（與 clients.verificationCategory[] 對齊），含 scheme
 *  - verificationSchemes.code = enum TOP/TAP/ECO，name max = 50，legalBasis max = 2000
 *  - contractSources.name max = 50（與 clients.contractSource 對齊）
 */
import { z } from 'zod';
/** 共用欄位（除 name 長度外），create 用。 */
const lookupBaseShape = {
    description: z.string().max(500).optional().nullable(),
    sortOrder: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
};
// ---------- 驗證類型 verification_types（原 client_qualifications） ----------
/**
 * 驗證類型字典新增 schema（對應 verification_types 表）。
 * 對應 clients.verificationType 多選陣列，例如「初次驗證」「再驗證」。
 */
export const createVerificationTypeSchema = z.object({
    name: z.string().min(1).max(50),
    ...lookupBaseShape,
});
export const updateVerificationTypeSchema = createVerificationTypeSchema.partial();
// ---------- 驗證資格 verification_qualifications（原 client_entity_types） ----------
/**
 * 驗證資格字典新增 schema（對應 verification_qualifications 表）。
 * 對應 clients.verificationQualification，例如「自然人」「農場」「農民團體」。
 */
export const createVerificationQualificationSchema = z.object({
    name: z.string().min(1).max(50),
    ...lookupBaseShape,
});
export const updateVerificationQualificationSchema = createVerificationQualificationSchema.partial();
// ---------- 驗證類別 verification_categories（原 verification_options，新增 scheme） ----------
/**
 * 驗證類別字典新增 schema（對應 verification_categories 表）。
 * 對應 clients.verificationCategory 多選陣列，依方案分類（如 TOP 有機農產品 / TAP 蔬菜）。
 * scheme 欄位限定該類別歸屬的驗證方案（TOP / TAP / ECO），不填表示通用。
 */
export const createVerificationCategorySchema = z.object({
    name: z.string().min(1).max(30),
    /** 歸屬驗證方案（TOP = 有機農產品；TAP = 產銷履歷；ECO = 生態服務） */
    scheme: z.enum(['TOP', 'TAP', 'ECO']).optional().nullable(),
    ...lookupBaseShape,
});
export const updateVerificationCategorySchema = createVerificationCategorySchema.partial();
// ---------- 驗證方案 verification_schemes（新） ----------
/**
 * 驗證方案字典新增 schema（對應 verification_schemes 表）。
 * code 為主鍵枚舉（TOP / TAP / ECO），name 為中文全名，legalBasis 為法源條文。
 */
export const createVerificationSchemeSchema = z.object({
    /** 方案代碼枚舉（TOP = 有機農產品；TAP = 產銷履歷；ECO = 生態服務） */
    code: z.enum(['TOP', 'TAP', 'ECO']),
    /** 方案中文名稱 */
    name: z.string().min(1).max(50),
    /** 法源條文（可空） */
    legalBasis: z.string().max(2000).optional().nullable(),
    ...lookupBaseShape,
});
export const updateVerificationSchemeSchema = createVerificationSchemeSchema.partial();
// ---------- 合約來源 contract_sources（不變） ----------
/**
 * 合約來源字典新增 schema（對應 contract_sources 表）。
 * 例如「中華驗證」「外部委託」。
 */
export const createContractSourceSchema = z.object({
    name: z.string().min(1).max(50),
    ...lookupBaseShape,
});
export const updateContractSourceSchema = createContractSourceSchema.partial();
